import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, ConnectionHandler, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {
  updateProjectItemFieldValueMutation,
  updateProjectItemFieldValueMutation$data,
  UpdateProjectV2ItemFieldValueInput,
} from './__generated__/updateProjectItemFieldValueMutation.graphql'
import {
  type OptimisticProjectItemFieldValue,
  projectItemFieldValueFromResponse,
  upsertProjectItemFieldValue,
} from './project-item-field-value-optimistic'

function scalarOptimisticFieldValue(
  input: UpdateProjectV2ItemFieldValueInput,
): OptimisticProjectItemFieldValue | undefined {
  const value = input.value
  const shared = {
    id: `optimistic-field-value-${input.itemId}-${input.fieldId}`,
    field: {id: input.fieldId},
  }

  if (value.text !== undefined) return {...shared, text: value.text}
  if (value.number !== undefined) return {...shared, number: value.number}
  if (value.date !== undefined) return {...shared, date: value.date}
  return undefined
}

function getSelectedMultiSelectOptions(
  fieldRecord: NonNullable<ReturnType<RecordSourceSelectorProxy['get']>>,
  selectedOptionIds: readonly string[],
) {
  const fieldOptions = fieldRecord.getLinkedRecords('multiSelectOptions') || []
  const selectedIds = new Set(selectedOptionIds)

  return fieldOptions.filter(option => {
    const id = option.getValue('id')
    return typeof id === 'string' && selectedIds.has(id)
  })
}

function updateProjectItemFieldValueOptimistic(
  store: RecordSourceSelectorProxy,
  input: UpdateProjectV2ItemFieldValueInput,
) {
  // step 1: try to get the field values connection for a project item
  // The connection may not exist if the field list accordion has not been opened yet.
  const connectionId = `${ConnectionHandler.getConnectionID(
    input.itemId,
    'ProjectItemSection_fieldValues',
  )}(orderBy:{"direction":"ASC","field":"POSITION"})`
  const connection = store.get(connectionId)

  // step 2: find the field value that matches the field id (only if the connection exists)
  let node
  if (connection) {
    const nodes = connection.getLinkedRecords('edges')?.map(edge => edge.getLinkedRecord('node')) || []
    node = nodes.find(cur_node => {
      if (!cur_node) return false
      const field = cur_node.getLinkedRecord('field')
      if (!field) return false
      return field.getDataID() === input.fieldId
    })
  }

  if (node) {
    // update the existing field value node
    switch (node.getType()) {
      case 'ProjectV2ItemFieldTextValue': {
        node.setValue(input.value.text, 'text')
        break
      }
      case 'ProjectV2ItemFieldNumberValue': {
        node.setValue(input.value.number, 'number')
        break
      }
      case 'ProjectV2ItemFieldDateValue': {
        node.setValue(input.value.date, 'date')
        break
      }
      case 'ProjectV2ItemFieldSingleSelectValue': {
        const fieldRecord = store.get(input.fieldId)
        if (!fieldRecord) return
        if (!input.value.singleSelectOptionId) return
        // get the option by id
        const singleSelectOption = fieldRecord
          .getLinkedRecords('options')
          ?.find(option => option.getValue('id') === input.value.singleSelectOptionId)
        if (!singleSelectOption) return

        node.setValue(input.value.singleSelectOptionId, 'optionId')

        for (const key of ['color', 'name', 'nameHTML']) {
          node.setValue(singleSelectOption.getValue(key), key)
        }

        break
      }
      case 'ProjectV2ItemFieldIterationValue': {
        if (!input.value.iterationId) return
        // get the iteration by id
        const iteration = store.get(input.value.iterationId)
        if (!iteration) return

        node.setValue(input.value.iterationId, 'iterationId')

        for (const key of ['duration', 'startDate', 'title', 'titleHTML']) {
          node.setValue(iteration.getValue(key), key)
        }

        break
      }
      case 'ProjectV2ItemFieldMultiSelectValue': {
        if (!input.value.multiSelectOptionIds) return

        const fieldRecord = store.get(input.fieldId)
        if (!fieldRecord) return

        const selectedOptions = getSelectedMultiSelectOptions(fieldRecord, input.value.multiSelectOptionIds)
        node.setLinkedRecords(selectedOptions, 'options')
        break
      }
    }
  } else {
    // no existing field value node — create one optimistically so the UI reflects the selection immediately
    const fieldRecord = store.get(input.fieldId)
    if (!fieldRecord) return

    const tempId = `optimistic-field-value-${input.itemId}-${input.fieldId}`
    let newNode

    if (input.value.singleSelectOptionId) {
      const singleSelectOption = fieldRecord
        .getLinkedRecords('options')
        ?.find(option => option.getValue('id') === input.value.singleSelectOptionId)
      if (!singleSelectOption) return

      newNode = store.create(tempId, 'ProjectV2ItemFieldSingleSelectValue')
      newNode.setValue(input.value.singleSelectOptionId, 'optionId')
      for (const key of ['color', 'name', 'nameHTML']) {
        newNode.setValue(singleSelectOption.getValue(key), key)
      }
      newNode.setLinkedRecord(fieldRecord, 'field')
    } else if (input.value.iterationId) {
      const iteration = store.get(input.value.iterationId)
      if (!iteration) return

      newNode = store.create(tempId, 'ProjectV2ItemFieldIterationValue')
      newNode.setValue(input.value.iterationId, 'iterationId')
      for (const key of ['duration', 'startDate', 'title', 'titleHTML']) {
        newNode.setValue(iteration.getValue(key), key)
      }
      newNode.setLinkedRecord(fieldRecord, 'field')
    } else if (input.value.text !== undefined && input.value.text !== null) {
      newNode = store.create(tempId, 'ProjectV2ItemFieldTextValue')
      newNode.setValue(input.value.text, 'text')
      newNode.setLinkedRecord(fieldRecord, 'field')
    } else if (input.value.number !== undefined && input.value.number !== null) {
      newNode = store.create(tempId, 'ProjectV2ItemFieldNumberValue')
      newNode.setValue(input.value.number, 'number')
      newNode.setLinkedRecord(fieldRecord, 'field')
    } else if (input.value.date !== undefined && input.value.date !== null) {
      newNode = store.create(tempId, 'ProjectV2ItemFieldDateValue')
      newNode.setValue(input.value.date, 'date')
      newNode.setLinkedRecord(fieldRecord, 'field')
    } else if (input.value.multiSelectOptionIds && input.value.multiSelectOptionIds.length > 0) {
      newNode = store.create(tempId, 'ProjectV2ItemFieldMultiSelectValue')
      const selectedOptions = getSelectedMultiSelectOptions(fieldRecord, input.value.multiSelectOptionIds)
      newNode.setLinkedRecords(selectedOptions, 'options')
      newNode.setLinkedRecord(fieldRecord, 'field')
    }

    if (newNode) {
      if (connection) {
        const edge = ConnectionHandler.createEdge(store, connection, newNode, 'ProjectV2ItemFieldValueEdge')
        ConnectionHandler.insertEdgeAfter(connection, edge)
      }

      // Also update fieldValueByName on the project item so the status field in
      // ProjectItemSectionView updates optimistically when setting a value for the first time
      const fieldName = fieldRecord.getValue('name')
      if (typeof fieldName === 'string') {
        const itemRecord = store.get(input.itemId)
        if (itemRecord) {
          itemRecord.setLinkedRecord(newNode, 'fieldValueByName', {name: fieldName})
        }
      }
    }
  }
}

export function commitUpdateProjectItemFieldValueMutation({
  environment,
  input,
  optimisticFieldValue,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: UpdateProjectV2ItemFieldValueInput
  optimisticFieldValue?: OptimisticProjectItemFieldValue
  onError?: (error: Error) => void
  onCompleted?: (response: updateProjectItemFieldValueMutation$data) => void
}) {
  const optimisticValue = optimisticFieldValue ?? scalarOptimisticFieldValue(input)
  const publication = publishOptimisticFields(
    optimisticValue ? upsertProjectItemFieldValue(input.itemId, input.fieldId, optimisticValue) : undefined,
  )

  const config: Parameters<typeof commitMutation<updateProjectItemFieldValueMutation>>[1] = {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateProjectItemFieldValueMutation($input: UpdateProjectV2ItemFieldValueInput!) @raw_response_type {
        updateProjectV2ItemFieldValue(input: $input) {
          projectV2Item {
            fieldValues(first: 50, orderBy: {field: POSITION, direction: ASC})
              @connection(key: "ProjectItemSection_fieldValues") {
              edges {
                node {
                  ... on ProjectV2ItemFieldTextValue {
                    id
                    text
                    field {
                      ... on ProjectV2Field {
                        id
                      }
                    }
                  }
                  ... on ProjectV2ItemFieldNumberValue {
                    id
                    number
                    field {
                      ... on ProjectV2Field {
                        id
                      }
                    }
                  }
                  ... on ProjectV2ItemFieldDateValue {
                    id
                    date
                    field {
                      ... on ProjectV2Field {
                        id
                      }
                    }
                  }
                  ... on ProjectV2ItemFieldSingleSelectValue {
                    id
                    field {
                      ... on ProjectV2SingleSelectField {
                        id
                      }
                    }
                    optionId
                    name
                    nameHTML
                    color
                  }
                  ... on ProjectV2ItemFieldIterationValue {
                    id
                    iterationId
                    title
                    titleHTML
                    startDate
                    duration
                    field {
                      ... on ProjectV2IterationField {
                        id
                        name
                        configuration {
                          iterations {
                            id
                            title
                            titleHTML
                            startDate
                            duration
                          }
                          completedIterations {
                            id
                            title
                            titleHTML
                            startDate
                            duration
                          }
                        }
                      }
                    }
                  }
                  ... on ProjectV2ItemFieldMultiSelectValue {
                    id
                    field {
                      ... on ProjectV2MultiSelectField {
                        id
                      }
                    }
                    options {
                      id
                      name
                      color
                    }
                  }
                }
              }
            }
            fieldValueByName(name: "Status") {
              ... on ProjectV2ItemFieldSingleSelectValue {
                name
              }
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: store => updateProjectItemFieldValueOptimistic(store, input),
    onError: error => {
      publication.failed(error)
      onError?.(error)
    },
    onCompleted: response => {
      const confirmedValue = projectItemFieldValueFromResponse(
        response.updateProjectV2ItemFieldValue?.projectV2Item?.fieldValues,
        input.fieldId,
      )
      if (!confirmedValue) {
        const error = new Error('Failed to update project item field value')
        publication.failed(error)
        onError?.(error)
        return
      }

      publication.completed(upsertProjectItemFieldValue(input.itemId, input.fieldId, confirmedValue))
      onCompleted?.(response)
    },
    onUnsubscribe: () => publication.failed(new Error('Updating project item field value was cancelled')),
  }

  try {
    return commitMutation<updateProjectItemFieldValueMutation>(environment, config)
  } catch (error) {
    publication.failed(error instanceof Error ? error : new Error('Failed to update project item field value'))
    throw error
  }
}
